import type { MaterialDto } from "@models/data/materialDto";
import type { EntityData, EntitySnapshot } from "@models/data/entityData";
import type { EntityUpdateKind } from "./IProjectModel";
import type { ProjectModel } from "./ProjectModel";

/**
 * `ProjectModelApplyServerChanges` — partial of {@link ProjectModel}
 * holding server-reconciliation paths for entities. Port of the relevant
 * bits of `Models/ProjectModel/ProjectModel_ApplyServerChanges.cs`.
 *
 * The web cloud transport (M5+) calls these methods when it receives
 * realtime events; locally-originated edits also funnel through the same
 * apply paths so the UI sees a single source of truth.
 *
 * Material / selection-set / media-tile / thumbnail apply paths land with
 * their respective milestones (M6/M7).
 */
export class ProjectModelApplyServerChanges {
  applyCreateEntity(
    this: ProjectModel,
    sceneId: string,
    entity: EntityData,
  ): void {
    const internals = this as unknown as Internals;
    const list = internals.m_entitiesByScene.get(sceneId) ?? [];
    if (list.find((e) => e.id === entity.id)) return;
    internals.m_entitiesByScene.set(sceneId, [...list, { ...entity, sceneId }]);
    bumpEntityCount(this, sceneId);
    this.onEntityChangesApplied.emit({
      sceneId,
      changeKind: "structure",
      entityId: entity.id,
    });
  }

  applyDeleteEntity(
    this: ProjectModel,
    sceneId: string,
    entityId: string,
  ): void {
    const internals = this as unknown as Internals;
    const list = internals.m_entitiesByScene.get(sceneId);
    if (!list) return;
    const next = list.filter((e) => e.id !== entityId);
    if (next.length === list.length) return;
    internals.m_entitiesByScene.set(sceneId, next);
    bumpEntityCount(this, sceneId);
    this.onEntityChangesApplied.emit({
      sceneId,
      changeKind: "structure",
      entityId,
    });
  }

  applyEntitySnapshotChanges(
    this: ProjectModel,
    sceneId: string,
    snapshot: EntitySnapshot,
  ): void {
    const internals = this as unknown as Internals;
    const list = internals.m_entitiesByScene.get(sceneId);
    if (!list) return;

    let previous: EntitySnapshot | null = null;
    let touchedEntityId: string | null = null;
    const next = list.map((entity) => {
      const updated = replaceSnapshot(entity.snapshots, snapshot, (found) => {
        previous ??= found;
      });
      if (updated === entity.snapshots) return entity;
      touchedEntityId = entity.id;
      return { ...entity, snapshots: updated };
    });

    if (!touchedEntityId) return;
    internals.m_entitiesByScene.set(sceneId, next);
    const updateKind = previous
      ? classifySnapshotChange(previous, snapshot)
      : "snapshot";
    this.onEntityUpdated.emit({
      sceneId,
      entityId: touchedEntityId,
      snapshotId: snapshot.id,
      updateKind,
    });
    this.onEntityChangesApplied.emit({
      sceneId,
      changeKind: "snapshot",
      entityId: touchedEntityId,
      snapshotId: snapshot.id,
    });

    // A peer can re-point a snapshot at a material row THIS client has never
    // fetched — routine since placements and the library-material fork mint
    // fresh rows. `applyMaterialChanges` deliberately no-ops for an unknown id
    // (Unity parity: `createMaterialIfRequired: false`), and an unresolvable
    // id does not degrade to "no material" — the renderer paints one
    // synthesized colour over the whole model. This method is synchronous by
    // contract, so hydrate in the background and re-emit once the rows land.
    hydrateUnknownMaterials(this, sceneId, snapshot);
  }
}

function hydrateUnknownMaterials(
  model: ProjectModel,
  sceneId: string,
  snapshot: EntitySnapshot,
): void {
  const state = model as unknown as Internals;
  const missing = snapshot.materialIds.filter(
    (id) => Boolean(id) && !state.m_materialsById.has(id),
  );
  if (missing.length === 0) return;
  void state
    .ensureMaterialsLoadedAsync(missing)
    .then(() => {
      // Still the same project, and the rows actually arrived.
      if (!state.m_entitiesByScene.has(sceneId)) return;
      // `onMaterialChangesApplied`, NOT `onEntityChangesApplied`: the renderer
      // answers the former with `applyMaterialToAllRendered`, and drops the
      // latter outright when `changeKind === "snapshot"`
      // (`sceneEntityRenderer.mount`, first line of that handler). Emitting
      // the entity event here would hydrate the cache and repaint nothing.
      for (const id of missing) {
        if (state.m_materialsById.has(id)) {
          model.onMaterialChangesApplied.emit({ materialId: id });
        }
      }
    })
    .catch(() => {
      /* non-fatal by contract — never lose a peer's edit over materials */
    });
}

function replaceSnapshot(
  snapshots: ReadonlyArray<EntitySnapshot>,
  replacement: EntitySnapshot,
  onReplace?: (previous: EntitySnapshot) => void,
): ReadonlyArray<EntitySnapshot> {
  for (let index = 0; index < snapshots.length; index++) {
    const snap = snapshots[index];
    if (!snap) continue;
    if (snap.id === replacement.id) {
      onReplace?.(snap);
      const next = [...snapshots];
      next[index] = replacement;
      return next;
    }
    const updatedChildren = replaceSnapshot(snap.children, replacement, onReplace);
    if (updatedChildren !== snap.children) {
      const next = [...snapshots];
      next[index] = { ...snap, children: updatedChildren };
      return next;
    }
  }
  return snapshots;
}

function classifySnapshotChange(
  previous: EntitySnapshot,
  next: EntitySnapshot,
): EntityUpdateKind {
  if (
    previous.id !== next.id
    || previous.name !== next.name
    || previous.displayName !== next.displayName
    || previous.kind !== next.kind
    || previous.partId !== next.partId
    || previous.isRoot !== next.isRoot
    || previous.hasMergedChildren !== next.hasMergedChildren
    || previous.visibility !== next.visibility
    || previous.forcedVisibility !== next.forcedVisibility
    || previous.isLocked !== next.isLocked
    || previous.isCollisionEnabled !== next.isCollisionEnabled
    || previous.explosionFactor !== next.explosionFactor
    || !sameTransform(previous.resetTransform, next.resetTransform)
    || !sameStrings(previous.materialIds, next.materialIds)
    || !sameAliases(previous.aliases, next.aliases)
    || previous.children !== next.children
    || previous.hierachyID !== next.hierachyID
  ) {
    return "snapshot";
  }

  const transformChanged = !sameTransform(previous.transform, next.transform);
  const spinChanged = previous.persistentSpinJson !== next.persistentSpinJson;
  const highlightChanged =
    previous.persistentHighlightJson !== next.persistentHighlightJson;
  const runtimeChangeCount =
    Number(transformChanged) + Number(spinChanged) + Number(highlightChanged);
  if (runtimeChangeCount !== 1) return "snapshot";
  if (transformChanged) return "transform";
  if (spinChanged) return "spin";
  return "highlight";
}

function sameTransform(
  a: EntitySnapshot["transform"],
  b: EntitySnapshot["transform"],
): boolean {
  return sameVec3(a.position, b.position)
    && sameVec3(a.rotation, b.rotation)
    && sameVec3(a.scale, b.scale);
}

function sameVec3(
  a: EntitySnapshot["transform"]["position"],
  b: EntitySnapshot["transform"]["position"],
): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

function sameStrings(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function sameAliases(
  a: EntitySnapshot["aliases"],
  b: EntitySnapshot["aliases"],
): boolean {
  return a.length === b.length
    && a.every(
      (value, index) =>
        value.language === b[index]?.language
        && value.value === b[index]?.value,
    );
}

function bumpEntityCount(model: ProjectModel, sceneId: string): void {
  const list = (model as unknown as Internals).m_entitiesByScene.get(sceneId);
  if (!list) return;
  const scene = model.getScene(sceneId);
  if (!scene) return;
  if (scene.entityCount === list.length) return;
  model.applySceneChanges({ ...scene, entityCount: list.length });
}

interface Internals {
  readonly m_entitiesByScene: Map<string, EntityData[]>;
  readonly m_materialsById: Map<string, MaterialDto>;
  ensureMaterialsLoadedAsync(
    materialIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void>;
}
